// App-Level Data Service
// Handles predefined names, movie status, email users, and week visibility
import { getSupabaseUrl, getSupabaseAnonKey } from '../config/supabase';
import { queuedSupabaseOperation, getSupabaseAuthHeaders } from '../utils/supabaseWrapper';

/**
 * Get Supabase configuration for direct API calls
 */
const getSupabaseConfig = () => {
  const supabaseUrl = getSupabaseUrl();
  const supabaseKey = getSupabaseAnonKey();
  
  if (!supabaseUrl || !supabaseKey) {
    throw new Error('Supabase configuration not found');
  }
  
  return { supabaseUrl, supabaseKey };
};

// ============================================================================
// PREDEFINED NAMES
// ============================================================================

/**
 * Load all predefined names
 */
export const loadPredefinedNames = async () => {
  try {
    const names = await queuedSupabaseOperation.select(
      'predefined_names',
      '*',
      'order=usage_count.desc,name.asc',
      null, // No localStorage fallback for select
      'Load predefined names'
    );
    
    const nameList = names.map(n => n.name);
    console.log('✅ Predefined names loaded from Supabase:', nameList.length, 'names');
    
    // Also save to localStorage as backup
    if (nameList.length > 0) {
      localStorage.setItem('predefined_names', JSON.stringify(nameList));
    }
    
    return nameList;
  } catch (error) {
    console.error('❌ Error loading predefined names:', error);
    // Fallback to localStorage
    const saved = localStorage.getItem('predefined_names');
    const fallbackNames = saved ? JSON.parse(saved) : [];
    console.log('🔄 Fallback to localStorage:', fallbackNames.length, 'names');
    return fallbackNames;
  }
};

/**
 * Add or increment usage count for a predefined name
 */
export const addPredefinedName = async (name) => {
  if (!name || !name.trim()) return { error: 'Name is required' };
  
  const trimmedName = name.trim();
  
  try {
    const { supabaseUrl } = getSupabaseConfig();
    
    // Try INSERT first with upsert behavior to increment usage count
    const response = await fetch(`${supabaseUrl}/rest/v1/predefined_names`, {
      method: 'POST',
      headers: await getSupabaseAuthHeaders({ 'Prefer': 'resolution=merge-duplicates,return=representation' }),
      body: JSON.stringify({
        name: trimmedName,
        usage_count: 1,
        updated_at: new Date().toISOString()
      })
    });

    // If conflict (409), increment existing usage count
    if (response.status === 409) {
      // First get current usage count
      const getResponse = await fetch(`${supabaseUrl}/rest/v1/predefined_names?name=eq.${encodeURIComponent(trimmedName)}&select=usage_count`, {
        method: 'GET',
        headers: await getSupabaseAuthHeaders()
      });
      
      if (getResponse.ok) {
        const existing = await getResponse.json();
        if (existing.length > 0) {
          const newCount = existing[0].usage_count + 1;
          
          // Update with incremented count
          const updateResponse = await fetch(`${supabaseUrl}/rest/v1/predefined_names?name=eq.${encodeURIComponent(trimmedName)}`, {
            method: 'PATCH',
            headers: await getSupabaseAuthHeaders({ 'Prefer': 'return=representation' }),
            body: JSON.stringify({
              usage_count: newCount,
              updated_at: new Date().toISOString()
            })
          });
          
          if (!updateResponse.ok) {
            throw new Error(`HTTP error! status: ${updateResponse.status}`);
          }
        }
      }
    } else if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    console.log('✅ Predefined name added/updated in Supabase:', trimmedName);
    return { error: null };
  } catch (error) {
    console.error('❌ Error adding predefined name:', error);
    return { error };
  }
};

// ============================================================================
// MOVIE STATUS
// ============================================================================

/**
 * Load all movie statuses
 */
export const loadMovieStatuses = async () => {
  try {
    const { supabaseUrl } = getSupabaseConfig();
    
    const response = await fetch(`${supabaseUrl}/rest/v1/movie_status?select=*&order=updated_at.desc`, {
      method: 'GET',
      headers: await getSupabaseAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const statuses = await response.json();
    console.log('✅ Movie statuses loaded from Supabase:', statuses.length, 'statuses');
    
    // Convert to the format expected by the app (object keyed by movie ID)
    const statusMap = {};
    statuses.forEach(status => {
      statusMap[status.movie_id] = status.status;
    });
    
    // Also save to localStorage as backup
    if (Object.keys(statusMap).length > 0) {
      localStorage.setItem('movie_statuses', JSON.stringify(statusMap));
    }
    
    return statusMap;
  } catch (error) {
    console.error('❌ Error loading movie statuses:', error);
    // Fallback to localStorage
    const saved = localStorage.getItem('movie_statuses');
    const fallbackStatuses = saved ? JSON.parse(saved) : {};
    console.log('🔄 Fallback to localStorage:', Object.keys(fallbackStatuses).length, 'statuses');
    return fallbackStatuses;
  }
};

/**
 * Set movie status
 */
export const setMovieStatus = async (movieId, status, updatedBy = null) => {
  console.log('🔍 setMovieStatus called with:', { movieId, status, updatedBy });
  
  try {
    // First try to update existing record
    try {
      console.log('🔄 Attempting to update movie status...');
      const updateResult = await queuedSupabaseOperation.update(
        'movie_status',
        {
          status: status,
          updated_by: updatedBy,
          updated_at: new Date().toISOString()
        },
        `movie_id=eq.${encodeURIComponent(movieId)}`,
        null, // No localStorage fallback for update
        `Update movie status: ${movieId} -> ${status}`
      );
      console.log('🔄 Update result:', updateResult, 'Rows affected:', updateResult?.length || 0);
      
      // Check if any rows were actually updated
      if (updateResult && updateResult.length > 0) {
        console.log('✅ Movie status updated in Supabase:', movieId, status, 'Result:', updateResult);
        return { error: null };
      } else {
        console.log('🔄 No rows updated, record doesn\'t exist. Trying insert...');
        // Throw an error to trigger the insert logic
        throw new Error('No rows updated - record does not exist');
      }
    } catch (updateError) {
      // If update fails (record doesn't exist), try insert
      console.log('🔄 Update failed, trying insert for:', movieId, 'Error:', updateError);
      
      const insertResult = await queuedSupabaseOperation.insert(
        'movie_status',
        {
          movie_id: movieId,
          status: status,
          updated_by: updatedBy,
          updated_at: new Date().toISOString()
          // Note: created_at will be auto-generated by the database DEFAULT
        },
        null, // No localStorage fallback for insert (will use manual fallback below)
        `Insert movie status: ${movieId} -> ${status}`
      );
      console.log('✅ Movie status inserted in Supabase:', movieId, status, 'Result:', insertResult);
      return { error: null };
    }
  } catch (error) {
    console.error('❌ Error saving movie status:', error);
    
    // Fallback to localStorage
    try {
      const statuses = JSON.parse(localStorage.getItem('movieStatuses') || '{}');
      statuses[movieId] = status;
      localStorage.setItem('movieStatuses', JSON.stringify(statuses));
      console.log('🔄 Movie status saved to localStorage fallback:', movieId, status);
      return { error: null };
    } catch (localError) {
      console.error('❌ Error saving movie status to localStorage:', localError);
      return { error: localError };
    }
  }
};

// ============================================================================
// EMAIL USERS
// ============================================================================

/**
 * Load all email users
 */
export const loadEmailUsers = async () => {
  try {
    const { supabaseUrl } = getSupabaseConfig();
    
    const response = await fetch(`${supabaseUrl}/rest/v1/email_users?select=*&order=last_active_at.desc`, {
      method: 'GET',
      headers: await getSupabaseAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const users = await response.json();
    console.log('✅ Email users loaded from Supabase:', users.length, 'users');
    
    // Convert to the format expected by the app (object keyed by email)
    const userMap = {};
    users.forEach(user => {
      userMap[user.email] = {
        email: user.email,
        name: user.name,
        createdAt: user.created_at || null,
        updatedAt: user.updated_at || null
      };
    });
    
    // Also save to localStorage as backup
    if (Object.keys(userMap).length > 0) {
      localStorage.setItem('survey_email_users', JSON.stringify(userMap));
    }
    
    return userMap;
  } catch (error) {
    console.error('❌ Error loading email users:', error);
    // Fallback to localStorage
    const saved = localStorage.getItem('survey_email_users');
    const fallbackUsers = saved ? JSON.parse(saved) : {};
    console.log('🔄 Fallback to localStorage:', Object.keys(fallbackUsers).length, 'users');
    return fallbackUsers;
  }
};

/**
 * Save or update email user
 */
export const saveEmailUser = async (email, name, profilePicture = null) => {
  try {
    console.log('🔍 DEBUG saveEmailUser called with:', {
      email,
      name,
      profilePicture: profilePicture ? 'present' : 'null'
    });
    
    const userData = {
      email: email.toLowerCase(),
      full_name: name
    };

    // Add profile picture if provided
    if (profilePicture) {
      userData.avatar_url = profilePicture;
      console.log('📝 Including profile picture in user data');
    }
    

    // Use queued upsert operation with fallback to localStorage
    const result = await queuedSupabaseOperation.upsert(
      'email_users', 
      userData, 
      'email',  // conflict column
      'email_users_fallback',  // fallback key
      `Save email user: ${email}`
    );

    if (result.error) {
      console.error('❌ Error saving email user via queue:', result.error);
      return { error: result.error };
    }

    console.log('✅ Email user saved successfully via queue:', email);
    return { error: null };

  } catch (error) {
    console.error('❌ Error in saveEmailUser:', error);
    return { error };
  }
};

/**
 * Update email user last active timestamp
 */
export const updateEmailUserActivity = async (email) => {
  try {
    // Since the email_users table doesn't have timestamp columns,
    // we'll skip the database update and just return success
    console.log('📝 Email user activity tracking skipped (no timestamp columns):', email);
    return { error: null };
  } catch (error) {
    console.error('❌ Error in updateEmailUserActivity:', error);
    return { error };
  }
};

// ============================================================================
// WEEK VISIBILITY
// ============================================================================

/**
 * Load week visibility settings
 */
export const loadWeekVisibility = async () => {
  try {
    const { supabaseUrl } = getSupabaseConfig();
    
    const response = await fetch(`${supabaseUrl}/rest/v1/week_visibility?select=*&order=updated_at.desc`, {
      method: 'GET',
      headers: await getSupabaseAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const visibility = await response.json();
    console.log('✅ Week visibility loaded from Supabase:', visibility.length, 'settings');
    
    // Convert to Set of visible week IDs (for backwards compatibility)
    const visibleWeeks = new Set();
    visibility.forEach(setting => {
      if (setting.is_visible) {
        visibleWeeks.add(setting.week_id);
      }
    });
    
    // Also save to localStorage as backup
    if (visibility.length > 0) {
      localStorage.setItem('week_visibility', JSON.stringify(Array.from(visibleWeeks)));
    }
    
    return visibleWeeks;
  } catch (error) {
    console.error('❌ Error loading week visibility:', error);
    // Fallback to localStorage
    const saved = localStorage.getItem('week_visibility');
    const fallbackVisibility = saved ? new Set(JSON.parse(saved)) : new Set();
    console.log('🔄 Fallback to localStorage:', fallbackVisibility.size, 'visible weeks');
    return fallbackVisibility;
  }
};

/**
 * Set week visibility
 */
export const setWeekVisibility = async (weekId, isVisible) => {
  try {
    console.log('🔍 setWeekVisibility called with:', { weekId, isVisible });
    
    // First try to update existing record
    try {
      console.log('🔄 Attempting to update week visibility...');
      const updateResult = await queuedSupabaseOperation.update(
        'week_visibility',
        {
          is_visible: isVisible,
          updated_at: new Date().toISOString()
        },
        `week_id=eq.${encodeURIComponent(weekId)}`,
        null, // No localStorage fallback for update
        `Update week visibility: ${weekId} -> ${isVisible}`
      );
      console.log('🔄 Update result:', updateResult, 'Rows affected:', updateResult?.length || 0);
      
      // Check if any rows were actually updated
      if (updateResult && updateResult.length > 0) {
        console.log('✅ Week visibility updated in Supabase:', weekId, isVisible, 'Result:', updateResult);
        return { error: null };
      } else {
        console.log('🔄 No rows updated, record doesn\'t exist. Trying insert...');
        // Throw an error to trigger the insert logic
        throw new Error('No rows updated - record does not exist');
      }
    } catch (updateError) {
      // If update fails (record doesn't exist), try insert
      console.log('🔄 Update failed, attempting insert...');
      
      const insertData = {
        week_id: weekId,
        is_visible: isVisible,
        updated_at: new Date().toISOString()
      };
      
      const insertResult = await queuedSupabaseOperation.insert(
        'week_visibility',
        insertData,
        null, // No localStorage fallback for insert (will use manual fallback below)
        `Insert week visibility: ${weekId} -> ${isVisible}`
      );
      console.log('✅ Week visibility inserted in Supabase:', weekId, isVisible, 'Result:', insertResult);
      return { error: null };
    }
  } catch (error) {
    console.error('❌ Error saving week visibility:', error);
    
    // Fallback to localStorage
    try {
      const saved = localStorage.getItem('week_visibility');
      const visibility = saved ? new Set(JSON.parse(saved)) : new Set();
      
      if (isVisible) {
        visibility.add(weekId);
      } else {
        visibility.delete(weekId);
      }
      
      localStorage.setItem('week_visibility', JSON.stringify(Array.from(visibility)));
      console.log('🔄 Week visibility saved to localStorage fallback:', weekId, isVisible);
      return { error: null };
    } catch (localError) {
      console.error('❌ Error saving week visibility to localStorage:', localError);
      return { error: localError };
    }
  }
};

